import React from 'react';
import { Building2, CheckCircle2, Clock3, CreditCard, Ban, Activity, MessageCircle, TrendingUp } from 'lucide-react';
import { MetricGrid, SectionHeader, StatCard } from '../shared';
import type { SuperAdminContext } from '../types';
import { smsRangeCostInr, smsUnitCost } from '../../lib/smsBilling';

export function OverviewSection({ ctx }: { ctx: SuperAdminContext }) {
  const { analytics, dateLabel } = ctx;
  const signups = analytics.signupsInPeriod ?? analytics.signups30d;
  const active = analytics.activeInPeriod ?? analytics.activeLast14d;
  const smsSent = analytics.smsMessagesTotal ?? analytics.whatsappMessagesTotal ?? 0;
  const smsUnit = smsUnitCost(analytics.smsUnitCostInr);
  const smsCost = smsRangeCostInr(smsSent, smsUnit, analytics.smsApiCostInr ?? analytics.whatsappApiCostInr);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Command center"
        subtitle="Platform-wide health at a glance — billing, adoption, API spend, and tenant activity for the selected period."
      />

      <MetricGrid>
        <StatCard label="Registered orgs" value={analytics.total} hint="All signups including pending checkout" icon={<Building2 className="h-4 w-4" />} />
        <StatCard label="Active accounts" value={analytics.activeBilling} tone="success" hint="Paying / active subscriptions" icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />} />
        <StatCard label="Pending checkout" value={analytics.pending} tone="warning" hint="Not counted in usage or storage" icon={<Clock3 className="h-4 w-4 text-amber-600" />} />
        <StatCard label="Past due" value={analytics.pastDue} tone="danger" hint="Needs collections follow-up" icon={<CreditCard className="h-4 w-4 text-rose-600" />} />
      </MetricGrid>

      <MetricGrid>
        <StatCard label="On hold" value={analytics.onHold} tone="danger" hint="Workspace access blocked" icon={<Ban className="h-4 w-4" />} />
        <StatCard label="Signups" value={signups} hint={`New orgs — ${dateLabel}`} icon={<TrendingUp className="h-4 w-4" />} />
        <StatCard label="Active tenants" value={active} tone="success" hint={`Workspace activity — ${dateLabel}`} icon={<Activity className="h-4 w-4 text-emerald-600" />} />
        <StatCard
          label="SMS cost (all shops)"
          value={`INR ${smsCost.toFixed(2)}`}
          tone="accent"
          hint={`${smsSent} SMS sent × ₹${smsUnit}`}
          icon={<MessageCircle className="h-4 w-4" />}
        />
      </MetricGrid>

      <div className="rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3 text-sm text-teal-950">
        <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">SMS pricing</p>
        <p className="mt-1 leading-relaxed">
          Bill each finance shop by <strong>SMS sent to its customers</strong> in the date range (Usage → open tenant),
          not by how many customers they have. Rate: <strong>₹{smsUnit}</strong> per successful SMS (25 paisa · platform
          env <code className="text-xs">SMS_UNIT_COST_INR</code>).
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Executive summary</p>
          <ul className="mt-3 space-y-2 text-sm text-zinc-700">
            <li>
              <strong>{analytics.activeBilling}</strong> active subscriber(s) of <strong>{analytics.total}</strong> registered organizations.
            </li>
            <li>
              <strong>{analytics.pending}</strong> pending checkout profile(s) are excluded from usage and storage until payment completes.
            </li>
            <li>
              <strong>{analytics.onHold}</strong> tenant(s) are on payment/compliance hold — review in Access control.
            </li>
            <li>
              SMS reminders: <strong>{analytics.smsMessagesTotal ?? analytics.whatsappMessagesTotal}</strong> messages
              sent across shops in the selected period (bill per shop from Usage).
            </li>
            <li>
              Platform stores <strong>{analytics.totalDataKeys}</strong> workspace data keys across all tenants.
            </li>
          </ul>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan mix (active only)</p>
          <ul className="mt-2 space-y-1.5 text-sm">
            {Object.entries(analytics.planMix)
              .sort((a, b) => b[1] - a[1])
              .map(([plan, n]) => (
                <li key={plan} className="flex justify-between gap-2 tabular-nums">
                  <span className="truncate text-zinc-700">{plan}</span>
                  <span className="font-semibold text-zinc-900">{n}</span>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
